import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import './toast.css';

type ToastTone = 'info' | 'ok' | 'warn' | 'error';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  detail?: string;
}

interface ToastApi {
  notify: (message: string, options?: { tone?: ToastTone; detail?: string }) => void;
  success: (message: string, detail?: string) => void;
  warn: (message: string, detail?: string) => void;
  error: (message: string, detail?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DISMISS_AFTER: Record<ToastTone, number> = {
  info: 4000,
  ok: 3500,
  // Fehler und Warnungen bleiben länger stehen — sie erfordern eine Reaktion.
  warn: 8000,
  error: 10_000,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback<ToastApi['notify']>(
    (message, options) => {
      const toast: Toast = {
        id: nextId.current++,
        tone: options?.tone ?? 'info',
        message,
        ...(options?.detail ? { detail: options.detail } : {}),
      };
      setToasts((current) => [...current, toast]);
      window.setTimeout(() => dismiss(toast.id), DISMISS_AFTER[toast.tone]);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      notify,
      success: (message, detail) => notify(message, { tone: 'ok', ...(detail ? { detail } : {}) }),
      warn: (message, detail) => notify(message, { tone: 'warn', ...(detail ? { detail } : {}) }),
      error: (message, detail) => notify(message, { tone: 'error', ...(detail ? { detail } : {}) }),
    }),
    [notify],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts no-print" role="region" aria-label="Meldungen">
        {toasts.map((toast) => (
          <output key={toast.id} className={`toast toast-${toast.tone}`}>
            <div className="toast-body">
              <span className="toast-message">{toast.message}</span>
              {toast.detail ? <span className="toast-detail">{toast.detail}</span> : null}
            </div>
            <button
              type="button"
              className="toast-close"
              onClick={() => dismiss(toast.id)}
              aria-label="Meldung schließen"
            >
              ✕
            </button>
          </output>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast muss innerhalb von ToastProvider verwendet werden.');
  return context;
}
